import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useSessionLanguage } from '@app/context/SessionSettingsContext'

import { useAccountReadContext } from '@context/AccountContextContext'

import { useTableSearch } from '@ui/v2/Table/hooks/useTableSearch'
import { FilterOptions } from '@ui/v2/TableFilter/TableFilter'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { ExportHubDto } from '@model/ExportHub'

import { ScrollToIds } from '@hooks/useScrollToElementIds'

import { IBReportResultsPage } from '../IBReportResultsPage'
import { ExportHubTable, ExportHubTableSortKeys } from './ExportHubTable'
import { ExportHubColumnCtx, exportHubColumns } from './ExportHubTable.columns'
import { useExportHub } from './hooks/useExportHub'

const useExportHubSearch = () =>
  useTableSearch<ExportHubTableSortKeys, ExportHubDto, ExportHubColumnCtx>({
    columns: exportHubColumns,
    placeholderKey: 'IB.Export.searchReportName',
    searchOptions: { customInitialSearchQuery: { ca_search_ReportName: '' } },
  })

const useFilterOptions = () => {
  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()
  const locale = useSessionLanguage()

  const ibId = account?.clientIntroducingBroker?.id

  const { data: filterOptions } = useQuery({
    queryKey: queryKeys.ib.exportHubFilterOptions(ibId, locale),
    queryFn: () => apiClient.getExportHubFilterOptions(ibId!, locale),
    enabled: !!ibId,
  })
  return { filterOptions }
}

export const ExportHubPage: React.FC = () => {
  const { t } = useTranslation()
  const search = useExportHubSearch()
  const data = useExportHub({ search })
  const { filterOptions } = useFilterOptions()

  return (
    <IBReportResultsPage
      title={t('Tabs.Export Hub')}
      data={data}
      search={search}
      scrollToHeaderId={ScrollToIds.IBSignupHeader}
      filterOptions={filterOptions}
      filterOptionsTypes={[
        FilterOptions.Section,
        FilterOptions.CreatedDate,
        FilterOptions.Format,
        FilterOptions.Status,
      ]}
      table={<ExportHubTable data={data} search={search} />}
    />
  )
}
